import { CubicBezier } from "../math/Bezier";
import { MathX } from "../math/MathX";
import { RationalCubicBezier } from "../math/RationalCubicBezier";

export abstract class AudioBufferChannelReader
{
  abstract sample( ab:AudioBuffer, position:number ): number;

  sampleSafe( ab:AudioBuffer, position:number )
  {
    if ( position < 0 || position > ab.getChannelData( 0 ).length )
    {
      return 0;
    }

    return this.sample( ab, position );
  }

  

  sampleTo( source:AudioBuffer, sourceOffset:number, target:Float32Array, targetOffset:number, length:number )
  {
    for ( var i = 0; i < length; i++ )
    {
      var sourceIndex = sourceOffset + i;
      var targetIndex = targetOffset + i;

      target[ targetIndex ] = this.sample( source, sourceIndex );
    }
  }

  sampleLerp( ab:AudioBuffer, position:number )
  {
    if ( position < 0 )
    {
      return 0;
    }

    if ( position >= ab.getChannelData( 0 ).length )
    {
      return 0;
    }

    let x0 = Math.floor( position );
    let x1 = x0 + 1;

    let lerp = position - x0;
    let value0 = this.sample( ab, x0 );
    let value1 = this.sample( ab, x1 );

    
    return MathX.lerpUnclamped( value0, value1, lerp ); 
  }

  protected _cubicSample1 = 0;
  protected _cubicSample2 = 0;
  protected _cubicTangent1 = 0;
  protected _cubicTangent2 = 0;

  getCubicValues( ab:AudioBuffer, position:number )
  {
    let s0 = this.sampleSafe( ab, position - 1 );
    let s1 = this.sample( ab, position );
    let s2 = this.sample( ab, position + 1);
    let s3 = this.sampleSafe( ab, position + 2 );
    
    let d1 = Math.sign( s1 - s0 );
    let d2 = Math.sign( s2 - s1 );
    let d3 = Math.sign( s3 - s2 );

    this._cubicSample1 = s1;
    this._cubicTangent1 = s1;    
    this._cubicTangent2 = s2;
    this._cubicSample2 = s2;

    if ( d1 === d2 )
    {
      this._cubicTangent1 = ( s1 + s2 ) * 0.5;
    }

    if ( d2 === d3 )
    {
      this._cubicTangent2 = ( s1 + s2 ) * 0.5;
    }
  }

  sampleCubic( ab:AudioBuffer, position:number )
  {
    if ( position < 0 )
    {
      return 0;
    }

    if ( position >= ab.getChannelData( 0 ).length )
    {
      return 0;
    }

    let x0 = Math.floor( position );

    let lerp = position - x0;


    this.getCubicValues( ab, x0 );

    let sample1 = this._cubicSample1;
    let sample2 = this._cubicSample2;

    let tangent1 = this._cubicTangent1;
    let tangent2 = this._cubicTangent2;

    
    return RationalCubicBezier.compute( lerp, sample1, tangent1, tangent2, sample2, 1, 1, 1, 1 );
  }
}

export class MiddleReader extends AudioBufferChannelReader
{
  sample( ab:AudioBuffer, position:number )
  {
    return ( ab.getChannelData( 0 )[ position ] + ab.getChannelData( 1 )[ position ] ) * 0.5;
  }
}

export class MaxAbsoluteReader extends AudioBufferChannelReader
{
  sample( ab:AudioBuffer, position:number )
  {
    var l = ab.getChannelData( 0 )[ position ];
    var r = ab.getChannelData( 1 )[ position ];
    return Math.max( Math.abs( l ), Math.abs( r ) );
  }
}

export class ExtremesReader extends AudioBufferChannelReader
{
  sample( ab:AudioBuffer, position:number )
  {
    var l = ab.getChannelData( 0 )[ position ];
    var r = ab.getChannelData( 1 )[ position ];
    return MathX.maxAbs( l, r );
  }
}

export class LeftReader extends AudioBufferChannelReader
{
  sample( ab:AudioBuffer, position:number )
  {
    return ab.getChannelData( 0 )[ position ];
  }
}

export class RightReader extends AudioBufferChannelReader
{
  sample( ab:AudioBuffer, position:number )
  {
    return ab.getChannelData( 1 )[ position ];
  }
}


